'use client';

import {
  type PropsWithChildren,
  createContext,
  useContext,
  useEffect
} from 'react';

import { BENTO_IMAGES_HOST } from 'reservations/account/constants';
import { useAccountQuery } from 'reservations/account/queries/useAccountQuery';
import type { SelectAccountResponse } from 'reservations/account/queries/useAccountQuery';
import { ErrorScreen } from 'reservations/base/components/ErrorScreen';
import { usePreviewModeContext } from 'reservations/preview/providers/PreviewModeProvider';

const AccountContext = createContext<SelectAccountResponse | null>(null);

const AccountProvider = ({ children }: PropsWithChildren) => {
  const { data, isPending, isError } = useAccountQuery();
  const { isPreview, previewData } = usePreviewModeContext();

  if (isPreview && data && previewData?.brand_image) {
    const previewBrandImage = previewData?.brand_image[0];

    if (previewBrandImage) {
      const { image_path, alt_text } = previewBrandImage;
      // for recent upload images, they come only with the path and not the full url
      const imageUrl = image_path?.startsWith('https')
        ? image_path
        : `${BENTO_IMAGES_HOST}/${image_path}`;

      data.brandImage = {
        alt_text: alt_text,
        url: imageUrl,
        image_large_url: imageUrl
      };
    }
  }

  useEffect(() => {
    if (data?.restaurantName) {
      document.title = data.restaurantName;
    }
  }, [data?.restaurantName]);

  useEffect(() => {
    if (data?.favicon_url) {
      const link = document.querySelector(
        "link[rel~='icon']"
      ) as HTMLLinkElement;
      if (link) {
        link.href = data.favicon_url;
      } else {
        // If the link tag is missing for some reason, create and append it
        const newLink = document.createElement('link');
        newLink.rel = 'icon';
        newLink.href = data.favicon_url;
        document.head.appendChild(newLink);
      }
    }
  }, [data?.favicon_url]);

  if (isPending) {
    return <></>;
  }

  // istanbul ignore next
  if (isError) {
    return <ErrorScreen />;
  }

  return (
    <AccountContext.Provider value={data}>{children}</AccountContext.Provider>
  );
};

export const useAccount = () => {
  const context = useContext(AccountContext);
  if (context == null) {
    throw new Error('useAccount must be used within AccountProvider');
  }

  return context;
};

export default AccountProvider;
